import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const DeepResearchIndicator = ({ 
  stage, 
  progress, 
  message, 
  reasoningTokens, 
  isVisible = true,
  onComplete,
  darkMode = false
}) => {
  const [displayedTokens, setDisplayedTokens] = useState(0);
  const [smoothedProgress, setSmoothedProgress] = useState(0);

  // Animate token counter
  useEffect(() => {
    if (reasoningTokens > 0) {
      const duration = 1000; // 1 second animation
      const steps = 50;
      const increment = reasoningTokens / steps;
      
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= reasoningTokens) {
          setDisplayedTokens(reasoningTokens);
          clearInterval(timer);
        } else {
          setDisplayedTokens(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }
  }, [reasoningTokens]);

  // Smart progress smoothing that feels natural without being deceptive
  useEffect(() => {
    const rawProgress = progress || 0;
    
    // Force completion when backend signals research is done
    if (stage === 'research_complete' || stage === 'complete') {
      // Animate to 100% when research is finalized
      const targetProgress = 1.0;
      const startProgress = smoothedProgress;
      const progressDiff = targetProgress - startProgress;
      
      if (Math.abs(progressDiff) > 0.001) {
        const animationDuration = 800; // Slightly longer for completion animation
        const steps = 30;
        const increment = progressDiff / steps;
        let stepCount = 0;
        
        const timer = setInterval(() => {
          stepCount++;
          const newProgress = startProgress + (increment * stepCount);
          
          if (stepCount >= steps) {
            setSmoothedProgress(1.0);
            clearInterval(timer);
          } else {
            setSmoothedProgress(newProgress);
          }
        }, animationDuration / steps);
        
        return () => clearInterval(timer);
      }
      return; // Skip normal progress mapping when completed
    }
    
    // OpenAI deep research specific mapping:
    // - Starts at ~20% (0.20) → should show as 0% visually
    // - Goes to ~23% (0.23) → should show as 50% visually  
    // - Goes to ~29% (0.29) → should show as ~90% visually
    // - Anything beyond 29% → 90-100% visually
    const mapOpenAIProgress = (raw) => {
      const OPENAI_START = 0.20;  // OpenAI starts at 20%
      const OPENAI_MID_POINT = 0.23;  // 23% shows as 50% visual
      const OPENAI_TYPICAL_END = 0.29;  // Typically ends around 29%
      const OPENAI_MAX_EXPECTED = 0.35;  // Buffer for longer runs
      
      // If we're below the start threshold, show minimal progress
      if (raw < OPENAI_START) {
        return Math.max(raw * 0.5, 0.02); // Show some progress but stay low
      }
      
      // Early stage: 20-23% OpenAI → 0-50% visual (fast ramp up)
      if (raw <= OPENAI_MID_POINT) {
        const normalizedProgress = (raw - OPENAI_START) / (OPENAI_MID_POINT - OPENAI_START);
        return normalizedProgress * 0.50; // Map to 0-50% visual range
      }
      
      // Mid stage: 23-29% OpenAI → 50-90% visual
      if (raw <= OPENAI_TYPICAL_END) {
        const normalizedProgress = (raw - OPENAI_MID_POINT) / (OPENAI_TYPICAL_END - OPENAI_MID_POINT);
        return 0.50 + (normalizedProgress * 0.40); // Map to 50-90% visual range
      }
      
      // Beyond typical end (29%+): 90-100% visual range
      if (raw <= OPENAI_MAX_EXPECTED) {
        const beyondNormal = (raw - OPENAI_TYPICAL_END) / (OPENAI_MAX_EXPECTED - OPENAI_TYPICAL_END);
        return 0.90 + (beyondNormal * 0.10); // Map remaining to 90-100%
      }
      
      // Edge case: if somehow goes beyond our max expected, cap at 95%
      return 0.95;
    };

    const targetProgress = mapOpenAIProgress(rawProgress);
    
    // Animate the progress change smoothly
    const startProgress = smoothedProgress;
    const progressDiff = targetProgress - startProgress;
    
    if (Math.abs(progressDiff) > 0.001) {
      const animationDuration = 600; // Slightly faster animation
      const steps = 25;
      const increment = progressDiff / steps;
      let stepCount = 0;
      
      const timer = setInterval(() => {
        stepCount++;
        const newProgress = startProgress + (increment * stepCount);
        
        if (stepCount >= steps) {
          setSmoothedProgress(targetProgress);
          clearInterval(timer);
        } else {
          setSmoothedProgress(newProgress);
        }
      }, animationDuration / steps);
      
      return () => clearInterval(timer);
    }
  }, [progress, smoothedProgress, stage]); // Added stage to dependencies

  // Stage configurations - simplified for black and white theme
  const stageConfig = {
    initialization: {
    
    },
    research: {
  
    },
    research_active: {
    },
    research_complete: {
   
    },
    analysis: {
    },
    complete: {
    },
    error: {
      icon: '❌'
    }
  };

  const config = stageConfig[stage] || stageConfig.initialization;
  const displayProgress = Math.round(smoothedProgress * 100);

  // Handle completion
  useEffect(() => {
    if (stage === 'complete' && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, 2000); // Wait 2 seconds before calling onComplete
      
      return () => clearTimeout(timer);
    }
  }, [stage, onComplete]);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={`
          w-full mx-auto p-4 rounded-2xl border bg-white border-gray-200 dark:border-[#282828] dark:bg-[#111111]
          shadow-sm backdrop-blur-sm
        `}
      >
        {/* Main indicator */}
        <div className="flex items-center">
          {/* Animated icon */}
          <motion.div
            animate={{ 
              scale: stage === 'research_active' ? [1, 1.1, 1] : 1,
              rotate: stage === 'research_active' ? [0, 5, -5, 0] : 0
            }}
            transition={{ 
              scale: { duration: 1.5, repeat: Infinity },
              rotate: { duration: 2, repeat: Infinity }
            }}
            className="text-2xl"
          >
            {config.icon}
          </motion.div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium dark:text-white text-black">
                <SkeletonTheme 
                  baseColor={darkMode ? "#666666" : "#999999"} 
                  highlightColor={darkMode ? "#ffffff" : "#ffffff"}
                >
                  <div 
                    style={{
                      width: `${Math.max(120, (message || 'Processing...').length * 8)}px`,
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      marginBottom: '8px'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '16px',
                        maskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                          <svg width="${Math.max(120, (message || 'Processing...').length * 8)}" height="16" viewBox="0 0 ${Math.max(120, (message || 'Processing...').length * 8)} 16" xmlns="http://www.w3.org/2000/svg">
                            <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" fill="black">${message || 'Processing...'}</text>
                          </svg>
                        `)}")`,
                        WebkitMaskImage: `url("data:image/svg+xml,${encodeURIComponent(`
                          <svg width="${Math.max(120, (message || 'Processing...').length * 8)}" height="16" viewBox="0 0 ${Math.max(120, (message || 'Processing...').length * 8)} 16" xmlns="http://www.w3.org/2000/svg">
                            <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="14" fill="black">${message || 'Processing...'}</text>
                          </svg>
                        `)}")`,
                        maskSize: 'contain',
                        WebkitMaskSize: 'contain',
                        maskRepeat: 'no-repeat',
                        WebkitMaskRepeat: 'no-repeat',
                        maskPosition: 'left center',
                        WebkitMaskPosition: 'left center'
                      }}
                    >
                      <div style={{ width: '100%', height: '100%' }}>
                        <Skeleton 
                          width="100%"
                          height="100%"
                          style={{ 
                            borderRadius: 0,
                            display: 'block',
                            margin: 0,
                            padding: 0,
                            lineHeight: '1',
                            verticalAlign: 'top'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </SkeletonTheme>
              </div>
              {/* Removed percentage display for better UX */}
            </div>

            {/* Progress bar with improved visual feedback */}
            <div className="mt-2 w-full bg-gray-200 dark:bg-[#282828] rounded-full h-2 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${displayProgress}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="h-full bg-gradient-to-r dark:from-white from-black dark:to-white to-black rounded-full shadow-sm"
              />
            </div>

            {/* Token counter */}
            {displayedTokens > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 text-xs text-gray-400"
              >
                Reasoning tokens: {displayedTokens.toLocaleString()}
              </motion.div>
            )}
          </div>
        </div>

        {/* Pulsing animation for active states */}
        {(stage === 'research_active' || stage === 'analysis') && (
          <motion.div
            animate={{ opacity: [0.1, 0.3, 0.1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 rounded-xl bg-white pointer-events-none"
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default DeepResearchIndicator; 